/**
 * EAMUS Font Awesome Shortcode Parser
 * Version 1.0
 *
 * Examples:
 *
 * [fa:check]
 * [fa:sparkles color=accent]
 * [fa:wand-magic-sparkles size=4x]
 * [fa:heart animation=beat color=red]
 * [fa:spinner animation=spin speed=1.5s]
 * [fa:location-dot transform="shrink-4 left-1"]
 *
 */

(() => {
  'use strict';

  /* -------------------------------------------------------
     CONFIG
  ------------------------------------------------------- */

  const TOKEN_REGEX = /\[fa:([a-z0-9-]+)([^\]]*)\]/gi;

  const SKIP_SELECTOR = [
    'script',
    'style',
    'textarea',
    'input',
    'select',
    'option',
    'code',
    'pre',
    'svg',
    'math',
    '[contenteditable="true"]',
    '[data-eamus-fa-ignore]'
  ].join(',');


  /* -------------------------------------------------------
     FONT AWESOME STYLES
  ------------------------------------------------------- */

  const STYLE_MAP = {
    'solid': ['fa-solid'],
    'regular': ['fa-regular'],
    'light': ['fa-light'],
    'thin': ['fa-thin'],
    'brands': ['fa-brands'],

    'sharp': ['fa-sharp', 'fa-solid'],
    'sharp-solid': ['fa-sharp', 'fa-solid'],
    'sharp-regular': ['fa-sharp', 'fa-regular'],
    'sharp-light': ['fa-sharp', 'fa-light'],
    'sharp-thin': ['fa-sharp', 'fa-thin']
  };


  /* -------------------------------------------------------
     FONT AWESOME ANIMATIONS
  ------------------------------------------------------- */

  const ANIMATION_MAP = {
    'beat': ['fa-beat'],
    'bounce': ['fa-bounce'],
    'fade': ['fa-fade'],
    'beat-fade': ['fa-beat-fade'],
    'flip': ['fa-flip'],
    'shake': ['fa-shake'],
    'spin': ['fa-spin'],
    'spin-pulse': ['fa-spin-pulse'],
    'spin-reverse': ['fa-spin', 'fa-spin-reverse']
  };


  /* -------------------------------------------------------
     EAMUS COLOR TOKENS

     Accent 可在 Dark Mode 裡直接換掉。
  ------------------------------------------------------- */

  const COLOR_MAP = {
    'inherit': 'currentColor',

    'accent': 'var(--eamus-fa-accent, currentColor)',
    'primary': 'var(--eamus-fa-primary, var(--eamus-fa-accent, currentColor))',
    'text': 'var(--eamus-fa-text, currentColor)',
    'muted': 'var(--eamus-fa-muted, currentColor)',

    'purple': '#62223E',
    'yellow': '#FFD80B',
    'green': '#A5BF5F',
    'red': '#F04E23',

    'dark': '#242528',
    'mid-dark': '#5A5A5C',
    'mid': '#909090',
    'mid-light': '#C6C5C3',
    'light': '#FCFAF7',

    'success': 'var(--eamus-fa-success, #A5BF5F)',
    'danger': 'var(--eamus-fa-danger, #F04E23)',
    'warning': 'var(--eamus-fa-warning, #FFD80B)'
  };


  /* -------------------------------------------------------
     FONT AWESOME SIZE CLASSES
  ------------------------------------------------------- */

  const SIZE_CLASSES = new Set([
    '2xs',
    'xs',
    'sm',
    'lg',
    'xl',
    '2xl',
    '1x',
    '2x',
    '3x',
    '4x',
    '5x',
    '6x',
    '7x',
    '8x',
    '9x',
    '10x'
  ]);


  /* -------------------------------------------------------
     UTILITIES
  ------------------------------------------------------- */

  function parseAttributes(raw = '') {

    const attributes = {};

    const regex =
      /([a-zA-Z][\w-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s]+))/g;

    let match;

    while ((match = regex.exec(raw)) !== null) {

      const key = match[1].toLowerCase();

      const value =
        match[2] ??
        match[3] ??
        match[4] ??
        '';

      attributes[key] = value;
    }

    return attributes;
  }


  function isTruthy(value) {

    return /^(1|true|yes|on)$/i.test(value || '');

  }


  function getColor(value) {

    if (!value) return null;

    const normalized = value.toLowerCase();

    if (COLOR_MAP[normalized]) {
      return COLOR_MAP[normalized];
    }

    /* Hex */

    if (/^#[0-9a-f]{3,8}$/i.test(value)) {
      return value;
    }

    /* rgb / rgba / hsl / hsla */

    if (/^(rgb|rgba|hsl|hsla)\([^)]+\)$/i.test(value)) {
      return value;
    }

    /* CSS variable */

    if (/^var\(--[\w-]+\)$/i.test(value)) {
      return value;
    }

    return null;
  }


  function applySize(icon, size) {

    if (!size) return;

    const normalized = size.toLowerCase();

    if (SIZE_CLASSES.has(normalized)) {

      icon.classList.add(`fa-${normalized}`);

      return;
    }

    /*
      Arbitrary CSS sizes:

      size=24px
      size=1.5em
      size=3rem
    */

    if (/^\d*\.?\d+(px|rem|em|%)$/i.test(size)) {

      icon.style.fontSize = size;

    }
  }


  function applyAnimation(icon, attributes) {

    if (!attributes.animation) return;

    const animation =
      attributes.animation.toLowerCase();

    const classes =
      ANIMATION_MAP[animation];

    if (classes) {
      icon.classList.add(...classes);
    }


    /* Animation duration */

    const speed =
      attributes.speed ||
      attributes.duration;

    if (
      speed &&
      /^\d*\.?\d+(ms|s)$/i.test(speed)
    ) {
      icon.style.setProperty(
        '--fa-animation-duration',
        speed
      );
    }


    /* Animation delay */

    if (
      attributes.delay &&
      /^\d*\.?\d+(ms|s)$/i.test(attributes.delay)
    ) {
      icon.style.setProperty(
        '--fa-animation-delay',
        attributes.delay
      );
    }


    /* Iteration count */

    const iterations =
      attributes.iterations ||
      attributes.repeat;

    if (
      iterations &&
      /^(infinite|\d+)$/i.test(iterations)
    ) {
      icon.style.setProperty(
        '--fa-animation-iteration-count',
        iterations
      );
    }


    /* Timing */

    if (attributes.timing) {

      const allowedTimings = [
        'linear',
        'ease',
        'ease-in',
        'ease-out',
        'ease-in-out'
      ];

      if (
        allowedTimings.includes(
          attributes.timing.toLowerCase()
        )
      ) {

        icon.style.setProperty(
          '--fa-animation-timing',
          attributes.timing
        );

      }
    }
  }


  /* -------------------------------------------------------
     BUILD ICON
  ------------------------------------------------------- */

  function buildIcon(iconName, rawAttributes) {

    const attributes =
      parseAttributes(rawAttributes);

    const icon =
      document.createElement('i');


    /* Mark generated icon */

    icon.dataset.eamusFa = 'true';


    /* ---------------------------
       STYLE
    --------------------------- */

    const styleName =
      (
        attributes.style ||
        'solid'
      ).toLowerCase();

    const styleClasses =
      STYLE_MAP[styleName] ||
      STYLE_MAP.solid;

    icon.classList.add(
      ...styleClasses
    );


    /* ---------------------------
       ICON
    --------------------------- */

    const cleanIconName =
      iconName.replace(/^fa-/, '');

    icon.classList.add(
      `fa-${cleanIconName}`
    );


    /* ---------------------------
       SIZE
    --------------------------- */

    applySize(
      icon,
      attributes.size
    );


    /* ---------------------------
       COLOR
    --------------------------- */

    const color =
      getColor(attributes.color);

    if (color) {
      icon.style.color = color;
    }


    /* ---------------------------
       OPACITY
    --------------------------- */

    if (attributes.opacity) {

      const opacity =
        parseFloat(attributes.opacity);

      if (
        !Number.isNaN(opacity) &&
        opacity >= 0 &&
        opacity <= 1
      ) {

        icon.style.opacity =
          opacity.toString();

      }
    }


    /* ---------------------------
       ANIMATION
    --------------------------- */

    applyAnimation(
      icon,
      attributes
    );


    /* ---------------------------
       POWER TRANSFORM
    --------------------------- */

    if (attributes.transform) {

      icon.setAttribute(
        'data-fa-transform',
        attributes.transform
      );

    }


    /* ---------------------------
       MASK
    --------------------------- */

    if (attributes.mask) {

      const maskName =
        attributes.mask
          .replace(/^fa-/, '')
          .toLowerCase();

      if (/^[a-z0-9-]+$/.test(maskName)) {

        icon.setAttribute(
          'data-fa-mask',
          `fa-solid fa-${maskName}`
        );

      }
    }


    /* ---------------------------
       ROTATE
    --------------------------- */

    if (
      ['90', '180', '270'].includes(
        attributes.rotate
      )
    ) {

      icon.classList.add(
        `fa-rotate-${attributes.rotate}`
      );

    }


    /* ---------------------------
       FLIP
    --------------------------- */

    if (attributes.flip) {

      const flip =
        attributes.flip.toLowerCase();

      if (flip === 'horizontal') {

        icon.classList.add(
          'fa-flip-horizontal'
        );

      }

      if (flip === 'vertical') {

        icon.classList.add(
          'fa-flip-vertical'
        );

      }

      if (flip === 'both') {

        icon.classList.add(
          'fa-flip-both'
        );

      }
    }


    /* ---------------------------
       FIXED WIDTH
    --------------------------- */

    if (
      isTruthy(attributes.fixed) ||
      isTruthy(attributes.fw)
    ) {

      icon.classList.add('fa-fw');

    }


    /* ---------------------------
       BORDER
    --------------------------- */

    if (
      isTruthy(attributes.border)
    ) {

      icon.classList.add(
        'fa-border'
      );

    }


    /* ---------------------------
       PULL LEFT / RIGHT
    --------------------------- */

    if (
      attributes.pull === 'left'
    ) {

      icon.classList.add(
        'fa-pull-left'
      );

    }

    if (
      attributes.pull === 'right'
    ) {

      icon.classList.add(
        'fa-pull-right'
      );

    }


    /* ---------------------------
       VERTICAL ALIGNMENT
    --------------------------- */

    if (
      attributes.valign &&
      /^-?\d*\.?\d+(px|rem|em|%)$/i.test(
        attributes.valign
      )
    ) {

      icon.style.verticalAlign =
        attributes.valign;

    }


    /* ---------------------------
       ACCESSIBILITY
    --------------------------- */

    const label =
      attributes.label ||
      attributes.title;

    if (label) {

      icon.setAttribute(
        'role',
        'img'
      );

      icon.setAttribute(
        'aria-label',
        label
      );

    } else {

      icon.setAttribute(
        'aria-hidden',
        'true'
      );

    }


    return icon;
  }


  /* -------------------------------------------------------
     PROCESS TEXT NODE
  ------------------------------------------------------- */

  function processTextNode(node) {

    const text =
      node.nodeValue;

    if (
      !text ||
      !text.includes('[fa:')
    ) {
      return;
    }


    const parent =
      node.parentElement;

    if (!parent) return;


    if (
      parent.closest(SKIP_SELECTOR)
    ) {
      return;
    }


    TOKEN_REGEX.lastIndex = 0;

    const fragment =
      document.createDocumentFragment();

    let lastIndex = 0;
    let match;
    let changed = false;


    while (
      (match = TOKEN_REGEX.exec(text)) !== null
    ) {

      const before =
        text.slice(
          lastIndex,
          match.index
        );

      if (before) {

        fragment.appendChild(
          document.createTextNode(before)
        );

      }


      const icon =
        buildIcon(
          match[1],
          match[2]
        );

      fragment.appendChild(icon);


      lastIndex =
        TOKEN_REGEX.lastIndex;

      changed = true;
    }


    if (!changed) return;


    const after =
      text.slice(lastIndex);

    if (after) {

      fragment.appendChild(
        document.createTextNode(after)
      );

    }


    node.replaceWith(fragment);
  }


  /* -------------------------------------------------------
     PARSE DOM AREA
  ------------------------------------------------------- */

  function parseWithin(root) {

    if (!root) return;


    /* Single text node */

    if (
      root.nodeType ===
      Node.TEXT_NODE
    ) {

      processTextNode(root);

      return;
    }


    if (
      root.nodeType !==
      Node.ELEMENT_NODE &&
      root.nodeType !==
      Node.DOCUMENT_NODE
    ) {
      return;
    }


    if (
      root.nodeType ===
      Node.ELEMENT_NODE &&
      root.matches(SKIP_SELECTOR)
    ) {
      return;
    }


    const walker =
      document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {

            if (
              !node.nodeValue ||
              !node.nodeValue.includes('[fa:')
            ) {

              return NodeFilter.FILTER_REJECT;

            }

            const parent =
              node.parentElement;

            if (
              !parent ||
              parent.closest(SKIP_SELECTOR)
            ) {

              return NodeFilter.FILTER_REJECT;

            }

            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );


    const nodes = [];

    let current;

    while (
      (current = walker.nextNode())
    ) {

      nodes.push(current);

    }


    nodes.forEach(
      processTextNode
    );
  }


  /* -------------------------------------------------------
     MUTATION OBSERVER

     Supports:
     - Shopify Theme Editor
     - AJAX sections
     - Dynamic blocks
     - Quick view
     - Product recommendations
  ------------------------------------------------------- */

  let scheduled = false;

  const pendingNodes =
    new Set();


  function queueParse(node) {

    if (!node) return;

    pendingNodes.add(node);

    if (scheduled) return;

    scheduled = true;


    requestAnimationFrame(() => {

      scheduled = false;

      const nodes =
        Array.from(pendingNodes);

      pendingNodes.clear();


      nodes.forEach(
        parseWithin
      );

    });
  }


  function startObserver() {

    if (!document.body) return;


    const observer =
      new MutationObserver(
        mutations => {

          mutations.forEach(
            mutation => {

              if (
                mutation.type ===
                'characterData'
              ) {

                queueParse(
                  mutation.target
                );

                return;
              }


              mutation.addedNodes.forEach(
                node => {

                  queueParse(node);

                }
              );

            }
          );

        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true,
        characterData: true
      }
    );
  }


  /* -------------------------------------------------------
     SHOPIFY THEME EDITOR EVENTS
  ------------------------------------------------------- */

  document.addEventListener(
    'shopify:section:load',
    event => {

      queueParse(
        event.target
      );

    }
  );


  document.addEventListener(
    'shopify:block:select',
    event => {

      queueParse(
        event.target
      );

    }
  );


  /* -------------------------------------------------------
     INITIALIZE
  ------------------------------------------------------- */

  function init() {

    parseWithin(
      document.body
    );

    startObserver();

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );

  } else {

    init();

  }


  /* -------------------------------------------------------
     PUBLIC API

     Manual use:
     EamusFA.parse(document.body)
  ------------------------------------------------------- */

  window.EamusFA = {
    parse: parseWithin
  };

})();